/**
 * kd-search-main のビジネスロジック（純粋関数）
 */

/**
 * collection.metafields.search_facet.initial.value (JSON 文字列) から
 * Algolia hierarchicalCategories filter 式を生成する。
 *
 * カテゴリ collection ページ (§17.3.2) で collection_ids の代わりに使用する。
 * - '{"category_lvl0":"食品"}' → 'hierarchicalCategories.lvl0:"食品"'（大カテゴリ collection）
 * - '{"category_lvl0":"食品","category_lvl1":"食品 > 肉類"}' → 'hierarchicalCategories.lvl1:"食品 > 肉類"'（中カテゴリ collection）
 *
 * 中カテゴリ collection で lvl0 と lvl1 を OR 結合しない理由:
 * lvl1 を持つ商品は必ず lvl0 も持つため、OR は lvl0 に absorption され
 * 意図しない大カテゴリ全商品がヒットしてしまう。
 *
 * @param initialRefinement - data-initial-refinement 属性の JSON 文字列
 */
export function buildInitialRefinementFilter(initialRefinement: string): string {
  if (!initialRefinement) return '';
  try {
    const ref = JSON.parse(initialRefinement) as {
      category_lvl0?: string;
      category_lvl1?: string;
    };
    // 中カテゴリ collection: lvl1 のみで絞り込む（より具体的なフィルタを優先）
    if (ref.category_lvl1) {
      return `hierarchicalCategories.lvl1:"${ref.category_lvl1}"`;
    }
    // 大カテゴリ collection: lvl0 で絞り込む
    if (ref.category_lvl0) {
      return `hierarchicalCategories.lvl0:"${ref.category_lvl0}"`;
    }
    return '';
  } catch {
    return '';
  }
}

/**
 * URL の `filter=` に大カテゴリ（lvl0）と、その配下の中カテゴリ（lvl1）が
 * 同時に含まれている場合、大カテゴリ側を落として中カテゴリを優先する（課題管理表 #68）。
 *
 * なぜ必要か: `_hydrateFromUrl` は URL の各 id に checked を直接立てるため、
 * kd-collection-filter の change ハンドラで行う親子排他を通らない。
 * 排他が効かないと上記 buildInitialRefinementFilter と同じ absorption が起きる
 * （lvl1 を持つ商品は必ず lvl0 も持つので `lvl0 OR lvl1` は lvl0 に吸収される）。
 * 共有・ブックマーク・戻る進むで来た既存URLは実際に親子ペアを含むため、
 * クリック経路だけを直しても中カテゴリで絞れない状態が残る。
 *
 * 子優先にする根拠: 起票者確認（2026-08-21）で「大カテゴリ側のチェックを自動で外す」を採択。
 * 親優先と混ぜると URL 内の並び順で結果が変わるため、**常に子優先**で解決する。
 *
 * 🔴 別の大カテゴリは落とさない。`食品,お菓子-child-1` は食品全体とチョコレートの
 * 両方を出すのが正（起票者確認済み）。落とすのは「その子の親」だけ。
 *
 * id の規約は `<親id>-child-<連番>`（snippets/v2-search-filter-category-row.liquid）。
 * クリック経路側の排他は collection-filter.logic.ts の getExclusiveCategoryIds が持つ。
 * 規約を変えるときは両方を直す。
 *
 * 🔴 **親を落とす根拠にできるのは「いま画面に存在する子」だけ。** 子の id は
 * `forloop.index` 由来の位置ベースなので、業務が中カテゴリを増減・並べ替えると
 * 既存URLの子 id が無効になる。存在確認をせずに親を落とすと、
 * `?filter=cat-food,cat-food-child-9`（child-9 が廃止済み）で
 * 親も子も適用されず**全商品表示**に落ちる。クリック経路側の
 * getExclusiveCategoryIds も同じ理由で親の存在を確認している。
 *
 * @param ids URL の `filter=` をカンマ分割した id 配列（`sp-` は付かない）
 * @param availableIds いま画面に存在する checkbox の filter-id（`sp-` 除去済み・重複可）
 * @returns 親子競合を解消した id 配列（入力の順序は保つ）
 */
export function resolveCategoryFilterIds(ids: string[], availableIds: string[]): string[] {
  const available = new Set(availableIds);
  const parentsToDrop = new Set<string>();
  for (const id of ids) {
    // 画面に無い id は無効。親を落とす根拠にしない
    if (!available.has(id)) continue;
    const asChild = id.match(/^(.+)-child-\d+$/);
    if (asChild) parentsToDrop.add(asChild[1]);
  }
  if (parentsToDrop.size === 0) return ids;
  return ids.filter((id) => !parentsToDrop.has(id));
}
